"use client";
import { BackButton } from "@/components/common/BackButton";

import * as React from "react";
import {
  Building2,
  ChevronRight,
  Globe2,
  Pencil,
  Plus,
  User,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { HeaderNotificationPopover } from "@/components/header/HeaderNotificationPopover";
import { HeaderTodoPopover } from "@/components/header/HeaderTodoPopover";
import { OrganizationLogo } from "@/components/organization-logo";
import { OrganizationLogoDialog } from "@/components/organization-logo-dialog";
import { Button } from "@/components/ui/button";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useOrganizationBranding } from "@/contexts/OrganizationBrandingContext";
import { usePageChrome } from "@/contexts/PageChromeContext";
import { resolvePageChrome } from "@/lib/pageChromeRoutes";
import { usePathname } from "@/lib/router-compat";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { isPlatformRole } from "@/lib/platformRoles";
import { useStaffMode } from "@/contexts/StaffModeContext";

type FacilityScope = {
  name: string;
  isAllFacilities: boolean;
};

type HeaderProps = {
  facilityScope?: FacilityScope;
};

const DEFAULT_FACILITY_SCOPE: FacilityScope = {
  name: "組織全体",
  isAllFacilities: true,
};

export function Header({
  facilityScope = DEFAULT_FACILITY_SCOPE,
}: HeaderProps = {}) {
  const pageChrome = usePageChrome();
  const { isMobile, state: sidebarState } = useSidebar();
  const { branding, isLoading: isBrandingLoading } = useOrganizationBranding();
  const { mode } = useStaffMode();
  const pathname = usePathname();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogoDialogOpen, setIsLogoDialogOpen] = React.useState(false);
  const fallbackChrome = resolvePageChrome(pathname);
  const mergedChrome = pageChrome
    ? {
        ...fallbackChrome,
        ...pageChrome,
        breadcrumbs: pageChrome.breadcrumbs ?? fallbackChrome.breadcrumbs,
      }
    : fallbackChrome;
  const resolvedChrome = {
    ...mergedChrome,
    breadcrumbs: mergedChrome.breadcrumbs?.filter(
      (item, index, items) =>
        !(index === items.length - 1 && item.label === mergedChrome.title)
    ),
  };
  const userRole = localAuthRepository.getUserInfo()?.role;
  const isPlatformUser = mode === "platform" || isPlatformRole(userRole);
  const isOrganizationAdmin =
    mode === "tenant" && userRole === "organization_admin";
  const isLogoDialogRequested =
    isOrganizationAdmin && searchParams.get("dialog") === "organization-logo";
  const logoDialogOpen = isLogoDialogOpen || isLogoDialogRequested;
  const organizationName = isPlatformUser
    ? "スマコミ運営"
    : (branding?.name ?? "組織");
  const FacilityIcon = facilityScope.isAllFacilities ? Globe2 : Building2;
  const showFacilityScope = isMobile || sidebarState === "collapsed";
  const registeredParent = resolvedChrome?.parentNavigation;
  const parentNavigation = (() => {
    if (!registeredParent?.href) return undefined;
    const targetPath = registeredParent.href
      .split(/[?#]/, 1)[0]
      .replace(/\/+$/, "");
    const currentPath = pathname.replace(/\/+$/, "");
    return targetPath !== currentPath ? registeredParent : undefined;
  })();

  const handleLogoDialogOpenChange = (open: boolean) => {
    setIsLogoDialogOpen(open);
    if (open || !isLogoDialogRequested) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("dialog");
    setSearchParams(nextSearchParams, { replace: true });
  };

  const organizationIdentity = (
    <>
      <span className="relative shrink-0">
        {isBrandingLoading && !branding ? (
          <span
            className="bg-muted block h-7 w-7 animate-pulse rounded-lg motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : (
          <OrganizationLogo
            organizationName={organizationName}
            logoUrl={isPlatformUser ? null : branding?.logoUrl}
            size="sm"
          />
        )}
        {isOrganizationAdmin ? (
          <span
            className="border-background bg-foreground text-background absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border-2"
            aria-hidden="true"
          >
            <Pencil className="size-2.5" />
          </span>
        ) : null}
      </span>
      <span className="text-foreground hidden min-w-0 truncate text-sm font-semibold sm:block">
        {isBrandingLoading && !branding ? "読み込み中…" : organizationName}
      </span>
    </>
  );

  return (
    <header className="border-border/90 bg-background/95 sticky top-0 z-40 h-16 shrink-0 border-b backdrop-blur-xl lg:h-14">
      <div className="flex h-full min-w-0 items-center gap-2 px-3 md:gap-3 md:px-4">
        <SidebarTrigger
          aria-label="サイドメニューを開く"
          className="h-11 w-11 shrink-0 lg:hidden"
        />

        {isOrganizationAdmin ? (
          <button
            type="button"
            aria-label="組織のブランド画像を管理"
            aria-haspopup="dialog"
            aria-expanded={logoDialogOpen}
            title="組織のブランド画像を管理"
            className="hover:bg-accent focus-visible:ring-ring flex min-h-11 min-w-0 max-w-44 shrink-0 cursor-pointer items-center gap-2.5 rounded-xl px-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:max-w-56"
            onClick={() => setIsLogoDialogOpen(true)}
          >
            {organizationIdentity}
          </button>
        ) : (
          <div
            role="group"
            aria-label="組織"
            className="flex min-w-0 max-w-44 shrink-0 items-center gap-2.5 sm:max-w-56"
            title={organizationName}
          >
            {organizationIdentity}
          </div>
        )}

        <div className="bg-border hidden h-8 w-px shrink-0 sm:block" />

        {showFacilityScope ? (
          <>
            <div
              role="group"
              aria-label="現在の表示範囲"
              className="text-muted-foreground hidden min-w-0 max-w-40 shrink-0 items-center gap-1.5 text-xs font-medium sm:flex"
              title={facilityScope.name}
            >
              <FacilityIcon
                className="text-brand h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              <span className="truncate">{facilityScope.name}</span>
            </div>
            <div className="bg-border hidden h-8 w-px shrink-0 sm:block" />
          </>
        ) : null}

        {resolvedChrome ? (
          <nav
            aria-label="パンくず"
            className="flex min-w-0 flex-1 items-center"
          >
            {parentNavigation?.href ? (
              <BackButton
                fallbackPath={parentNavigation.href}
                ariaLabel={
                  parentNavigation.label.endsWith("戻る")
                    ? parentNavigation.label
                    : `${parentNavigation.label}へ戻る`
                }
                className="mr-1 shrink-0 xl:hidden"
              >
                戻る
              </BackButton>
            ) : null}
            <ol className="text-muted-foreground flex w-full min-w-0 items-center gap-1 text-xs">
              {resolvedChrome.breadcrumbs?.map((item) => (
                <li
                  key={`${item.label}-${item.href ?? ""}`}
                  className="hidden min-w-0 items-center gap-1 xl:flex"
                >
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="hover:text-foreground truncate transition-colors"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span className="truncate">{item.label}</span>
                  )}
                  <ChevronRight
                    className="h-3 w-3 shrink-0"
                    aria-hidden="true"
                  />
                </li>
              ))}
              <li className="min-w-0 flex-1" aria-current="page">
                <span
                  className="text-muted-foreground block truncate text-[10px] font-medium leading-tight sm:hidden"
                  aria-hidden="true"
                >
                  {facilityScope.name} · {organizationName}
                </span>
                <h1 className="text-foreground truncate text-sm font-bold focus:outline-none md:text-base">
                  {resolvedChrome.title}
                </h1>
              </li>
            </ol>
          </nav>
        ) : (
          <div className="flex-1" />
        )}

        {resolvedChrome?.primaryAction ? (
          <Button
            asChild
            variant="primaryAction"
            size="sm"
            className="h-11 min-w-11 shrink-0 px-3 sm:min-w-0 lg:h-10"
          >
            <Link
              to={resolvedChrome.primaryAction.href}
              aria-label={resolvedChrome.primaryAction.label}
            >
              {resolvedChrome.primaryAction.icon === "plus" ? (
                <Plus className="h-4 w-4" aria-hidden="true" />
              ) : null}
              <span
                className={
                  resolvedChrome.primaryAction.icon ? "hidden sm:inline" : ""
                }
              >
                {resolvedChrome.primaryAction.label}
              </span>
            </Link>
          </Button>
        ) : null}

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <HeaderTodoPopover />
          <HeaderNotificationPopover />
          <Button
            variant="outline"
            size="icon"
            className="hover:text-brand text-muted-foreground h-11 w-11 lg:h-10 lg:w-10"
            asChild
          >
            <Link to="/settings/account" aria-label="アカウント設定">
              <User className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
      {isOrganizationAdmin ? (
        <OrganizationLogoDialog
          open={logoDialogOpen}
          onOpenChange={handleLogoDialogOpenChange}
        />
      ) : null}
    </header>
  );
}
